import { TitleCasePipe } from '@angular/common';
import { Component, ElementRef, EventEmitter, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  convertAbbreviations,
  customDecode,
  greaterThanZeroValidator,
  nonNegativeValidator,
  noWhitespaceValidator,
} from '@piqet-angular/data';
import { CustomModalService, FileUploadComponent, FileUploadResult } from '@piqet-angular/shared';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'piqet-angular-new-add-sub-product',
  templateUrl: './new-add-sub-product.component.html',
  styleUrls: ['./new-add-sub-product.component.scss'],
})
export class NewAddSubProductComponent {
  @ViewChild('fileUploadComponent') fileUploadComponent!: FileUploadComponent;
  @Output() saveNewMyProductProjectEvent = new EventEmitter<any>();
  modalLabel: any;
  submitted = false;
  productProjectForm!: FormGroup;
  productProjectData: any;
  countryList: any;
  // File upload properties
  selectedFiles: string | null = null;
  fileBase64: string | null = null;
  existingImageUrl: string | null = null;
  existingFileName: string | null = null;

  selectedCountry: any;
  unitList: any;
  selectedCurrency: any;
  unitId: any;
  currencyList: any;
  showCurrencyList: any;
  isCostTurnedOff: boolean = false;
  currencyItem: any;
  productConfigId: any;
  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly modal: CustomModalService,
    private readonly titleCasePipe: TitleCasePipe,
    private toaster: ToastrService
  ) {
    this.modalLabel = this.modal?.modalService?.config?.initialState?.['label'];
    this.productProjectData =
      this.modal?.modalService?.config?.initialState?.['newMyProductProjects'];
    this.unitList = this.productProjectData.unitList;
    this.countryList = this.productProjectData.countryItems;
    this.currencyList = this.productProjectData?.currencyItems;
    const url = location.href.split('/');
    this.productConfigId = Number(atob(decodeURIComponent(customDecode(url[6]))));
    this.countryList = this.countryList.map((x: any) => ({
      ...x,
      name: this.titleCasePipe.transform(x.name.split('(')[0]),
    }));
    this.countryList = convertAbbreviations(this.countryList);
    this.mapCountryList();
    this.setCurrency();
    this.initializeExistingFile();
  }

  private initializeExistingFile(): void {
    if (this.productProjectData?.productContentsImage) {
      this.existingImageUrl = this.productProjectData.productImageUrl || null;
      this.existingFileName = this.productProjectData.productContentsFile || null;
      this.fileBase64 = this.productProjectData.productContentsImage;
      this.selectedFiles = this.existingFileName;
    }
  }

  ngOnInit(): void {
    this.productProjectForm = this.formBuilder.group({
      productName: ['', [Validators.required, noWhitespaceValidator]],
      productNameReport: [
        '',
        [Validators.required, noWhitespaceValidator, Validators.maxLength(15)],
      ],
      country: [null, Validators.required],
      description: [null],
      amount: [null, [Validators.required, nonNegativeValidator]],
      density: [1, [Validators.required, greaterThanZeroValidator()]],
      unit: [null, Validators.required],
      currency: [null],
    });

    this.setFormData();
  }

  async mapCountryList() {
    await this.productProjectData?.countryList?.map((item: any) => {
      if (item.id == this.productProjectData.countryId) {
        return (this.selectedCountry = item);
      }
    });
  }

  async setCurrency() {
    await this.currencyList?.map((data: any) => {
      if (data?.id == this.productProjectData?.currencyId) {
        return (this.currencyItem = data);
      }
    });
  }

  setFormData() {
    this.isCostTurnedOff = !this.productProjectData.isTurnOffCost;
    if (this.productProjectData) {
      // Set country
      if (this.productProjectData.countryId) {
        const selectedCountry = this.countryList.find(
          (country: any) => country.id === this.productProjectData.countryId
        );
        this.productProjectForm.patchValue({
          country: selectedCountry,
        });
      }

      // Set unit
      if (this.productProjectData.unitId) {
        const selectedUnit = this.unitList.find(
          (unit: any) => unit.id === this.productProjectData.unitId
        );
        this.productProjectForm.patchValue({
          unit: selectedUnit,
        });
        this.unitId = this.productProjectData.unitId;
      }

      // Set cost switch and currency
      this.showCurrencyList = !this.productProjectData.isTurnOffCost;
      if (this.productProjectData.currencyId) {
        const selectedCurrency = this.currencyList.find(
          (currency: any) => currency.id === this.productProjectData.currencyId
        );
        this.selectedCurrency = this.productProjectData.currencyId;
        this.productProjectForm.patchValue({
          currency: selectedCurrency,
        });
      }
    }
    this.productProjectForm.patchValue({ currency: this.currencyItem });
  }

  getProductname(value: any) {
    this.productProjectForm.controls['productNameReport'].setValue(value);
  }

  commonInputSearch(term: string, item: any) {
    return item.name.toLowerCase().includes(term.toLowerCase());
  }

  changeUnit(eve: any) {
    if (eve?.id) {
      this.unitId = eve?.id;
    }
  }

  costTurnOff(event: any) {
    if (event.target.checked && !this.productProjectData.currencyId) {
      // FOR setting USD as default currency if none is selected
      this.productProjectForm
        ?.get('currency')
        ?.setValue(this.productProjectData?.currencyItems?.find((x: any) => x.id == 1));
    }
    this.showCurrencyList = event.target.checked ? true : false;
  }

  selectCurrency(event: any) {
    if (!!event.id) {
      this.selectedCurrency = event?.id;
    }
  }

  onFileSelected(result: FileUploadResult): void {
    if (result.isValid) {
      this.selectedFiles = result.fileName;
      this.fileBase64 = result.fileBase64;
    }
  }

  onRemoveImage(): void {
    this.selectedFiles = null;
    this.fileBase64 = null;
    this.existingImageUrl = null;
    this.existingFileName = null;

    // Reset the file upload component
    if (this.fileUploadComponent) {
      this.fileUploadComponent.reset();
    }

    this.toaster.success('Image has been deleted');
  }

  onClose() {
    this.modal.close();
  }

  onSave() {
    this.submitted = true;
    if (!this.productProjectForm.valid) {
      return;
    }
    const projecPayload = {
      productId: this.productProjectData.productId || 0,
      projectId: this.productProjectData.projectId || 0,
      name: this.productProjectForm.controls['productName'].value,
      shortProductName: this.productProjectForm.controls['productNameReport'].value,
      countryId: this.productProjectForm.controls['country'].value?.id,
      productContentsFile: this.selectedFiles,
      productContentsImage: this.fileBase64,
      amountOfproduct: this.productProjectForm.controls['amount'].value,
      densityOfProduct: this.productProjectForm.controls['density'].value,
      unitId: this.unitId,
      description: this.productProjectForm.controls['description'].value,
      currencyId: this.selectedCurrency || this.currencyItem?.id,
      isTurnOffCost: !this.isCostTurnedOff,
      productConfigId: this.productConfigId,
    };
    this.saveNewMyProductProjectEvent.emit(projecPayload);
    this.modal.close();
  }
}
